import { useState } from 'react';
import { AlertCircle, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { DrawerSection } from '@/components/drawer/DrawerSection';
import { CASH_PAYMENT_TYPES, PaymentTypeField } from '@/components/form/PaymentTypeField';
import { PdfUploadField, type UploadedFile } from '@/components/form/PdfUploadField';
import { getApiErrorMessage } from '@/api/errorHandler';
import { PolicyCloseRequestMapper } from '@/api/mapper/policy';
import { closePolicy } from '@/api/service/policy';
import type { CashPaymentType } from '@/api/type/payment';
import type { Policy } from '@/types';
import { formatCurrency } from '@/lib/payments';
import { todayDateDisplay } from '@/lib/dates';
import { cn } from '@/lib/utils';
import { LABEL_CLASS, PRIMARY_BUTTON_CLASS } from '@/lib/styles';

interface ClosePolicyFormProps {
  policy: Policy;
  interestEarned: number;
  totalValue: number;
  /** The member's balance due; it is taken out of the payout. */
  balanceDue: number;
  onClosed: () => void;
}

const CLOSE_TYPES = [
  { value: 'close', label: 'Close Policy', hint: 'Member takes the policy amount', disabled: false },
  { value: 'deceased', label: 'Deceased', hint: 'Coming soon', disabled: true },
] as const;

const formatRupees = (amount: number) => `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

/** Pays the member the policy amount plus interest and closes the policy. */
export function ClosePolicyForm({ policy, interestEarned, totalValue, balanceDue, onClosed }: ClosePolicyFormProps) {
  // The server deducts the same amount; this shows the admin what the member will receive.
  const balanceDeducted = Math.min(balanceDue, totalValue);
  const payout = totalValue - balanceDeducted;
  const [payoutType, setPayoutType] = useState<CashPaymentType>('Cash');
  const [closureDoc, setClosureDoc] = useState<UploadedFile | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleClose = async () => {
    if (!closureDoc) {
      setError('Please upload the policy closure document (PDF)');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await closePolicy(
        Number(policy.id),
        PolicyCloseRequestMapper({ closeType: 'close', payoutType, closureDocUrl: closureDoc.url })
      );
      onClosed();
    } catch (closeError) {
      setError(getApiErrorMessage(closeError));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DrawerSection icon={FileText} title="Close Policy" description="Pay the policy amount and interest to the member">
      {balanceDue > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          This member has a balance of {formatCurrency(balanceDue)}. It will be deducted from this payout.
        </div>
      )}

      {policy.paidMonths < policy.totalMonths && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          This policy is not completed yet ({policy.paidMonths} / {policy.totalMonths} months paid).
        </div>
      )}

      <div className="space-y-3">
        <Label className={LABEL_CLASS}>Close Type *</Label>
        <div className="grid grid-cols-2 gap-3">
          {CLOSE_TYPES.map((type) => (
            <div
              key={type.value}
              className={cn(
                'flex items-start gap-3 rounded-lg border px-4 py-3',
                type.disabled
                  ? 'cursor-not-allowed border-gray-200 bg-gray-50 opacity-60'
                  : 'border-[#4369a0]/40 bg-[#4369a0]/5 ring-1 ring-[#4369a0]/20'
              )}
            >
              <span
                className={cn(
                  'mt-0.5 flex h-4 w-4 items-center justify-center rounded-full border',
                  type.disabled ? 'border-gray-300' : 'border-[#4369a0]'
                )}
              >
                {!type.disabled && <span className="h-2 w-2 rounded-full bg-[#4369a0]" />}
              </span>
              <div>
                <p className="text-sm font-medium text-gray-900">{type.label}</p>
                <p className="text-xs text-gray-500">{type.hint}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <PaymentTypeField<CashPaymentType>
        options={CASH_PAYMENT_TYPES}
        id="close-payout"
        label="Paid As *"
        cashHint="Handed over in person"
        value={payoutType}
        onChange={setPayoutType}
      />

      <dl className="divide-y divide-gray-100 border-t border-gray-100">
        {[
          ['Policy Paid', formatCurrency(policy.totalPaid)],
          ['Interest Earned', formatRupees(interestEarned)],
          ['Close Date', todayDateDisplay()],
        ].map(([label, value]) => (
          <div key={label} className="flex items-center justify-between gap-6 py-3">
            <dt className="text-sm text-gray-500">{label}</dt>
            <dd className="text-sm font-medium tabular-nums text-gray-900">{value}</dd>
          </div>
        ))}
        {balanceDeducted > 0 && (
          <div className="flex items-center justify-between gap-6 py-3">
            <dt className="text-sm text-gray-500">Less: Member Balance</dt>
            <dd className="text-right text-sm font-medium tabular-nums text-amber-900">
              − {formatRupees(balanceDeducted)}
              <span className="block text-xs font-normal text-gray-500">
                {balanceDue > balanceDeducted
                  ? `${formatRupees(balanceDue - balanceDeducted)} will stay on the balance`
                  : 'Balance will be cleared'}
              </span>
            </dd>
          </div>
        )}
        <div className="flex items-center justify-between gap-6 py-3">
          <dt className="text-sm text-gray-500">Total to Pay Member</dt>
          <dd className="text-base font-semibold tabular-nums text-teal-800">{formatRupees(payout)}</dd>
        </div>
      </dl>

      <PdfUploadField
        label="Policy Closure Document (PDF) *"
        caption="Policy closure document"
        value={closureDoc}
        onChange={setClosureDoc}
      />

      <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 px-4 py-3">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
          className="mt-0.5 h-4 w-4 accent-[#4369a0]"
        />
        <span>
          <span className="block text-sm font-medium text-gray-900">I have paid {formatRupees(payout)} to the member</span>
          <span className="text-xs text-gray-500">
            The policy will be closed and no more payments, interest or loans will be allowed.
          </span>
        </span>
      </label>

      {error && (
        <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={handleClose}
          disabled={!confirmed || !closureDoc || submitting}
          className={`min-w-36 ${PRIMARY_BUTTON_CLASS}`}
        >
          {submitting ? 'Closing...' : 'Close Policy'}
        </Button>
      </div>
    </DrawerSection>
  );
}
